import React from 'react'
import { REDOC_OPERATION_BUTTON_SELECTOR } from '@/utils/redoc/RedocApiConfig'

/**
 * Utility functions for handling API operation buttons in Redoc UI
 * These functions help locate, modify, and manage the behavior of API endpoint buttons
 * in the Redoc documentation interface
 */

/**
 * Finds all API endpoint operation buttons in the Redoc UI by their CSS class and returns them as an array
 *
 * @returns {HTMLButtonElement[]} Array of API endpoint operation button elements found in the document
 */
export function findApiOperationButtons(): HTMLButtonElement[] {
  const redocOperationButtons = document.querySelectorAll(
    REDOC_OPERATION_BUTTON_SELECTOR
  ) as NodeListOf<HTMLButtonElement>
  
  if (redocOperationButtons && redocOperationButtons.length > 0) {
    return Array.from(redocOperationButtons)
  }
  
  return []
}

/**
 * Handles the delayed search for API operation buttons and updates state with found buttons
 * Uses a recursive setTimeout approach to wait for Redoc to fully render buttons
 *
 * @param {React.Dispatch<React.SetStateAction<HTMLButtonElement[]>>} setApiOperationButtons - React state setter for API operation buttons
 * @param {React.MutableRefObject<HTMLButtonElement[]>} apiOperationButtonsRef - React ref to persistently store API operation buttons
 * @returns {HTMLButtonElement[]} Current array of API endpoint operation buttons from the reference
 */
export function handleApiOperationButtonSearch(
  setApiOperationButtons: React.Dispatch<React.SetStateAction<HTMLButtonElement[]>>,
  apiOperationButtonsRef: React.MutableRefObject<HTMLButtonElement[]>
): HTMLButtonElement[] {
  // Wait for the Redoc content to be fully loaded
  setTimeout(function delayedOperationButtonSearch(): void {
    const foundOperationButtons = findApiOperationButtons()

    // Store the buttons in the ref for access outside the timeout
    apiOperationButtonsRef.current = foundOperationButtons

    if (foundOperationButtons.length > 0) {
      setApiOperationButtons(foundOperationButtons)

      // Signal buttons are ready to have handlers removed
      removeOperationButtonClickHandlers(foundOperationButtons)

      // Set relative position to sibling elements
      setOperationButtonSiblingsPositionRelative(foundOperationButtons)
    } else {
      // If no buttons found yet, try again after a delay
      setTimeout(
        () => handleApiOperationButtonSearch(setApiOperationButtons, apiOperationButtonsRef),
        1000
      )
    }
  }, 1500)

  return apiOperationButtonsRef.current
}

/**
 * Removes all click event handlers from the provided API operation buttons by cloning each button
 * This effectively wipes out any event handlers attached to the buttons
 *
 * @param {HTMLButtonElement[]} operationButtons - Array of API endpoint operation button elements to process
 * @returns {void}
 */
export function removeOperationButtonClickHandlers(
  operationButtons: HTMLButtonElement[]
): void {
  if (!operationButtons || operationButtons.length === 0) {
    return
  }

  operationButtons.forEach(function processOperationButton(
    operationButton: HTMLButtonElement
  ): void {
    // Click the button to render the dropdown
    operationButton.click()

    // Clone the button to remove all event listeners
    const clonedOperationButton = operationButton.cloneNode(true) as HTMLButtonElement

    // Check and set sibling position to relative
    if (operationButton.nextElementSibling) {
      const operationButtonSibling = operationButton.nextElementSibling as HTMLElement

      if (operationButtonSibling) {
        operationButtonSibling.style.position = 'relative'
      }
    }

    // Replace the original button with the clone
    if (operationButton.parentNode) {
      operationButton.parentNode.replaceChild(clonedOperationButton, operationButton)
    }
  })
}

/**
 * Sets the CSS position attribute to "relative" for the first sibling element of each button
 * This helps maintain proper positioning of dropdown elements in the Redoc UI
 *
 * @param {HTMLButtonElement[]} operationButtons - Array of API endpoint operation button elements to process
 * @returns {void}
 */
export function setOperationButtonSiblingsPositionRelative(
  operationButtons: HTMLButtonElement[]
): void {
  if (!operationButtons || operationButtons.length === 0) {
    return
  }

  operationButtons.forEach(function processOperationButtonSibling(
    operationButton: HTMLButtonElement
  ): void {
    // Look for the button's next sibling element directly
    if (operationButton.nextElementSibling) {
      const operationButtonSibling = operationButton.nextElementSibling as HTMLElement

      if (operationButtonSibling) {
        operationButtonSibling.style.position = 'relative'
      }
    }
  })
}
